// Generic run-history persistence for the Catalog Manager workflows.
//
// ============================================================================
// STORAGE NOTE (read me before extending):
// This is the ONE swap seam for run history. Every workflow that wants to keep
// its runs (grocery replacement, web sourcing finder, ...) stores them here,
// namespaced by a `kind` and scoped to the signed-in user. Today the backing
// store is the browser's localStorage; the day we move it server-side (a
// `catalog_runs` table keyed by customer + kind + run id), swap ONLY the
// `readRaw`/`writeRaw` pair below (or reimplement loadRuns/saveRuns) and the
// rest of the UI keeps working unchanged.
//
// NOTE: the older `ingestJobs.ts` predates this and keeps its own (identical-in-
// spirit) store because it also drives background polling; it can be migrated
// onto this later. New workflows should use THIS module.
// ============================================================================

/** Which workflow a stored run belongs to. */
export type RunKind = "ingest" | "replacement" | "sourcing";

export type RunStatus = "running" | "completed" | "failed" | "cancelled";

/** Status of one actionable item within a run (e.g. written / added). */
export interface RunItemStatus {
  /** Free-form state string, workflow-defined (e.g. "added", "written"). */
  state: string;
  at: number;
  /** Optional resulting id (e.g. the created food id). */
  id?: string;
  note?: string;
}

/**
 * One persisted run. `payload` holds whatever the workflow needs to fully
 * re-open the run (its input + results); `itemStatus` tracks per-item actions
 * taken afterward (so, e.g., re-opening a finished run shows which items were
 * already ingested).
 */
export interface StoredRun<TPayload = unknown> {
  id: string;
  kind: RunKind;
  status: RunStatus;
  /** Short human label (first item + count). */
  label: string;
  createdAt: number;
  updatedAt: number;
  /** One-line echo of the input (e.g. the grocery list's first line). */
  inputSummary?: string;
  /** EasyAI chain execution id, when known (links to run history on easyai). */
  chainRunId?: string;
  credits?: number | null;
  error?: string;
  /** Everything needed to reload the run (input + results). */
  payload?: TPayload;
  /** Per-item action status, keyed by a workflow-defined item key. */
  itemStatus?: Record<string, RunItemStatus>;
  /** Arbitrary rollup counts for the tray/panel (e.g. { added: 3, total: 8 }). */
  counts?: Record<string, number>;
}

/** Keep at most this many runs per kind (newest kept); older ones drop. */
const MAX_STORED_RUNS = 25;

const LS_PREFIX = "catalogManager.runs";

// Scope storage to the signed-in user so one machine's users don't see each
// other's runs. Falls back to a shared key when no user id is available.
let userScope = "anon";
export const setRunStoreUserScope = (id?: string): void => {
  userScope =
    (id || "anon").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64) || "anon";
};

/** localStorage key for a given kind (per current user). */
export const runStorageKey = (kind: RunKind): string =>
  `${LS_PREFIX}.${kind}.${userScope}`;

/** True when the given event targets any run-store key (for cross-tab sync). */
export const isRunStoreKey = (key: string | null | undefined): boolean =>
  !!key && key.startsWith(LS_PREFIX);

const isTerminal = (s: RunStatus): boolean =>
  s === "completed" || s === "failed" || s === "cancelled";

/** Read all runs of a kind for the current user (newest first). */
export const loadRuns = <TPayload = unknown>(
  kind: RunKind
): StoredRun<TPayload>[] => {
  try {
    const raw = window.localStorage.getItem(runStorageKey(kind));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as StoredRun<TPayload>[])
      .filter((r) => r && typeof r.id === "string")
      .sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
};

/**
 * Persist the run list for a kind. Caps the count and, on a localStorage quota
 * error (result payloads can be large), progressively sheds the heaviest
 * payloads (oldest terminal runs' payloads) before giving up — the run metadata
 * is far more important to keep than a re-loadable old payload.
 */
export const saveRuns = <TPayload = unknown>(
  kind: RunKind,
  runs: StoredRun<TPayload>[]
): void => {
  const trimmed = [...runs]
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, MAX_STORED_RUNS);

  const attempt = (list: StoredRun<TPayload>[]): boolean => {
    try {
      window.localStorage.setItem(runStorageKey(kind), JSON.stringify(list));
      return true;
    } catch {
      return false;
    }
  };

  if (attempt(trimmed)) return;

  // Quota hit — drop payloads from the oldest terminal runs first.
  const lighten = trimmed
    .map((r) => ({ ...r }))
    .sort((a, b) => a.createdAt - b.createdAt);
  for (const r of lighten) {
    if (isTerminal(r.status) && r.payload !== undefined) {
      delete r.payload;
      const reordered = [...lighten].sort((a, b) => b.createdAt - a.createdAt);
      if (attempt(reordered)) return;
    }
  }
  // Last resort: keep just the most recent few, metadata only.
  attempt(
    trimmed.slice(0, 5).map(({ payload, ...rest }) => {
      void payload;
      return rest as StoredRun<TPayload>;
    })
  );
};

/** A short, friendly label for a run from its first line + item count. */
export const labelForRun = (input: string, count: number): string => {
  const firstLine = (input || "")
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  let head = firstLine || "run";
  if (head.length > 40) head = `${head.slice(0, 40)}…`;
  return count > 1 ? `${head} +${count - 1} more` : head;
};
